/**
 * Watches agentIntents in stream values and processes them via AgentIntentProcessor.
 *
 * Parameterized by name so any page can use it:
 *   useAgentIntentWatcher("brainstorm")
 *   useAgentIntentWatcher("website")
 *
 * Works inside any Chat.Root — uses useStreamSelector.
 */
import { useEffect, useRef } from "react";
import { useStreamSelector } from "@launch10/use-langgraph-stream";
import { AgentIntentProcessor } from "@lib/AgentIntentProcessor";
import type { AgentIntent } from "@shared";

export function useAgentIntentWatcher(name: string) {
  // Cast values to any — this hook is generic across all graph states,
  // but all our graphs include agentIntents in their state type.
  const agentIntents = useStreamSelector(
    (s) => (s.values as any)?.agentIntents as AgentIntent[] | undefined
  );

  const processorRef = useRef<AgentIntentProcessor | null>(null);

  if (!processorRef.current) {
    processorRef.current = AgentIntentProcessor.forName(name);
  }

  // Mark existing intents as processed ONLY on history load (revisit guard).
  // If a live stream is running (isLoading), intents are new and should be processed.
  const isLoading = useStreamSelector((s) => s.isLoading);
  const markedRef = useRef(false);
  if (!markedRef.current && !isLoading && agentIntents?.length && processorRef.current) {
    processorRef.current.markProcessed(agentIntents);
    markedRef.current = true;
  }

  useEffect(() => {
    if (agentIntents?.length && processorRef.current) {
      processorRef.current.process(agentIntents);
    }
  }, [agentIntents]);
}
